/** Approximate age range for person research intake (TruePeopleSearch `agerange`). */

export type ParsedAgeRange = {
  min: number;
  max: number;
  serialized: string;
  midpoint: number;
  tolerance: number;
};

export const AGE_MIN = 1;
export const AGE_MAX = 119;
/** Example bounds for docs/tests only — never prefill the intake form with these. */
export const DEFAULT_AGE_RANGE_FROM = 25;
export const DEFAULT_AGE_RANGE_TO = 55;
export const DEFAULT_AGE_RANGE = `${DEFAULT_AGE_RANGE_FROM}-${DEFAULT_AGE_RANGE_TO}`;

function clampAge(value: number): number {
  return Math.max(AGE_MIN, Math.min(AGE_MAX, Math.round(value)));
}

export function parseAgeRange(value: string | null | undefined): ParsedAgeRange | null {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) {
    return null;
  }

  const pair = text.match(/(\d{1,3})\s*(?:-|–|—|to|\/)\s*(\d{1,3})/i);
  if (pair) {
    let min = Number(pair[1]);
    let max = Number(pair[2]);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null;
    }
    // Do not swap while the upper bound still has fewer digits than the lower
    // bound — typing 20 then 30 briefly looks like 20-3.
    if (min > max) {
      if (String(pair[2]).length < String(pair[1]).length) {
        return null;
      }
      [min, max] = [max, min];
    }
    min = clampAge(min);
    max = clampAge(max);
    if (max - min >= 80) {
      return null;
    }
    return {
      min,
      max,
      serialized: `${min}-${max}`,
      midpoint: Math.round((min + max) / 2),
      tolerance: Math.max(1, Math.ceil((max - min) / 2)),
    };
  }

  const single = text.match(/^(?:about|approx(?:imately)?|~)?\s*(\d{1,3})$/i);
  if (single) {
    const age = Number(single[1]);
    if (!Number.isFinite(age) || age < AGE_MIN || age > AGE_MAX) {
      return null;
    }
    const min = clampAge(age - 5);
    const max = clampAge(age + 5);
    return {
      min,
      max,
      serialized: `${min}-${max}`,
      midpoint: clampAge(age),
      tolerance: 5,
    };
  }

  return null;
}

export function ageRangeDraftParts(value: string | null | undefined): { min: string; max: string } {
  const draft = String(value || '').trim().match(/^(\d{0,3})-(\d{0,3})$/);
  if (draft) {
    return { min: draft[1], max: draft[2] };
  }
  const parsed = parseAgeRange(value);
  if (parsed) {
    return { min: String(parsed.min), max: String(parsed.max) };
  }
  return { min: '', max: '' };
}

/** @deprecated Placeholders belong on the inputs; never fill empty as 25–55. */
export function ageRangeDisplayParts(value: string | null | undefined): { min: string; max: string } {
  return ageRangeDraftParts(value);
}

export function ageRangeIsTooWide(value: string | null | undefined): boolean {
  const draft = String(value || '').trim().match(/^(\d{1,3})-(\d{1,3})$/);
  if (!draft) {
    return false;
  }
  const min = Number(draft[1]);
  const max = Number(draft[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return false;
  }
  return Math.abs(max - min) >= 80;
}

/** Seed From/To defaults on first stepper click, then apply the new number. */
export function stepAgeRange(current: string, side: 'min' | 'max', nextRaw: string): string {
  const seeded = String(current || '').trim() || DEFAULT_AGE_RANGE;
  return updateAgeRangeDraft(seeded, side, nextRaw);
}

export function updateAgeRangeDraft(current: string, side: 'min' | 'max', nextRaw: string): string {
  const next = String(nextRaw || '').replace(/\D/g, '').slice(0, 3);
  const parts = ageRangeDraftParts(current);
  const min = side === 'min' ? next : parts.min;
  const max = side === 'max' ? next : parts.max;
  if (!min && !max) {
    return '';
  }
  const minNum = Number(min);
  const maxNum = Number(max);
  if (min && max && Number.isFinite(minNum) && Number.isFinite(maxNum) && minNum <= maxNum) {
    const parsed = parseAgeRange(`${min}-${max}`);
    if (parsed) {
      return parsed.serialized;
    }
  }
  return `${min}-${max}`;
}

export function normalizeAgeRangeForSubmit(value: string | null | undefined): string {
  const text = String(value || '').trim();
  const parsed = parseAgeRange(text);
  if (parsed) {
    return parsed.serialized;
  }
  const onlyMin = text.match(/^(\d{1,3})-$/);
  if (onlyMin) {
    return parseAgeRange(onlyMin[1])?.serialized || '';
  }
  const onlyMax = text.match(/^-(\d{1,3})$/);
  if (onlyMax) {
    return parseAgeRange(onlyMax[1])?.serialized || '';
  }
  return '';
}
